import { useEffect, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { LoadingBlock, BusyLabel } from '../components/Spinner'
import { Modal } from '../dynamicLists/shared'
import type { CrmAccount } from '../dynamicLists/types'
import type { TrackingLinksApi } from './api'
import { DEFAULT_REPORT_COLUMNS, REPORT_COLUMNS, type LinkKind, type LinkRef, type LinkReport, type LinkRow, type ReportColumn } from './types'

const PICK_LIMIT = 500
const KINDS: LinkKind[] = ['tracking', 'trial']
const key = (l: { account_id: string; kind?: LinkKind; link_id: string }) => `${l.account_id}/${l.kind ?? 'tracking'}/${l.link_id}`
const refOf = (k: string): LinkRef => {
  const [account_id, kind, link_id] = [k.slice(0, k.indexOf('/')), k.slice(k.indexOf('/') + 1, k.lastIndexOf('/')), k.slice(k.lastIndexOf('/') + 1)]
  return { account_id, kind: kind === 'trial' ? 'trial' : 'tracking', link_id }
}

/**
 * A Link report is its own thing: a name, the links it shows (from any Accounts,
 * of either kind), which columns the viewer sees, and an optional expiry. Revoking
 * kills the URL. Tracking and trial links show as separate sections.
 */
export function ReportDialog({
  api,
  accounts,
  report,
  initialLinks = [],
  onClose,
  onSaved,
}: {
  api: TrackingLinksApi
  accounts: CrmAccount[] | null
  report: LinkReport | null
  /** Links a new report starts with (from a row's share button). */
  initialLinks?: LinkRef[]
  onClose: () => void
  onSaved: (report: LinkReport, isNew: boolean) => void
}) {
  const { t } = useTranslation('trackingLinks')
  const [name, setName] = useState(report?.name ?? '')
  const [columns, setColumns] = useState<ReportColumn[]>(report?.columns ?? [...DEFAULT_REPORT_COLUMNS])
  const [expires, setExpires] = useState(report?.expires_at ? report.expires_at.slice(0, 10) : '')
  const [picked, setPicked] = useState<string[]>(() => (report?.links ?? initialLinks).map(key))
  const [kind, setKind] = useState<LinkKind>(() => (report?.links ?? initialLinks)[0]?.kind ?? 'tracking')
  const [search, setSearch] = useState('')
  const [links, setLinks] = useState<LinkRow[] | null>(null)
  const [loadError, setLoadError] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ids = useId()

  useEffect(() => {
    let live = true
    setLinks(null)
    const timer = window.setTimeout(() => {
      api.list({ kind, search: search.trim(), includeDeleted: true, archived: 'any', earning: 'net', limit: PICK_LIMIT }).then(
        (r) => live && (setLinks(r.links), setLoadError('')),
        (e: unknown) => live && setLoadError(e instanceof Error ? e.message : String(e)),
      )
    }, 300)
    return () => {
      live = false
      window.clearTimeout(timer)
    }
  }, [api, search, kind])

  // Names for picked links, kept even when a search or the other kind hides them.
  const [known, setKnown] = useState<Map<string, LinkRow>>(new Map())
  useEffect(() => {
    if (!links) return
    setKnown((m) => {
      const next = new Map(m)
      for (const l of links) next.set(key(l), l)
      return next
    })
  }, [links])

  // Picked links of the other kind are named too, before its list is ever opened.
  useEffect(() => {
    let live = true
    const other: LinkKind = kind === 'tracking' ? 'trial' : 'tracking'
    if (!picked.some((p) => refOf(p).kind === other)) return
    api.list({ kind: other, includeDeleted: true, archived: 'any', earning: 'net', limit: PICK_LIMIT }).then(
      (r) => live && setKnown((m) => new Map([...m, ...r.links.map((l) => [key(l), l] as const)])),
      () => {},
    )
    return () => {
      live = false
    }
    // Once, when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const groups = useMemo(() => {
    const by = new Map<string, LinkRow[]>()
    for (const l of links ?? []) by.set(l.account_id, [...(by.get(l.account_id) ?? []), l])
    return [...by.entries()]
  }, [links])

  const toggle = (k: string) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]))
  const toggleColumn = (c: ReportColumn) =>
    setColumns((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : REPORT_COLUMNS.filter((x) => cs.includes(x) || x === c)))

  const expiresIso = expires ? new Date(`${expires}T23:59:59Z`).toISOString() : null
  const problem = !name.trim()
    ? t('report.nameNeeded')
    : picked.length === 0
      ? t('report.pickLinks')
      : columns.length === 0
        ? t('report.pickColumns')
        : expiresIso && Date.parse(expiresIso) <= Date.now()
          ? t('report.expiryPast')
          : ''

  const save = async () => {
    setBusy(true)
    setError('')
    const draft = { name: name.trim(), columns, expires_at: expiresIso, links: picked.map(refOf) }
    try {
      const r = report ? await api.updateReport(report.id, draft) : await api.createReport(draft)
      onSaved(r.report, !report)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  const accountLabel = (id: string) => {
    const a = accounts?.find((x) => x.account_id === id)
    return a ? `${a.profile_name || a.username || id}${a.username ? ` · @${a.username}` : ''}` : id
  }
  const pickedOf = (k: LinkKind) => picked.filter((p) => refOf(p).kind === k).length

  return (
    <Modal
      wide
      title={report ? t('report.editTitle') : t('report.newTitle')}
      onClose={onClose}
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="dl-btn dl-btn--primary" disabled={busy || Boolean(problem)} title={problem || undefined} onClick={() => void save()}>
            <BusyLabel busy={busy} label={t('saving')}>{report ? t('report.save') : t('report.create')}</BusyLabel>
          </button>
        </>
      }
    >
      <div className="tl-form">
        <div className="dl-field">
          <label className="dl-label" htmlFor={`${ids}-name`}>{t('report.name')}</label>
          <input id={`${ids}-name`} className="dl-input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
            placeholder={t('report.namePlaceholder')} aria-describedby={`${ids}-name-hint`} autoFocus />
          <span id={`${ids}-name-hint`} className="dl-hint">{t('report.nameHint')}</span>
        </div>

        <div className="dl-field">
          <span className="dl-label">
            {t('report.links')} <span className="dl-muted">{t('report.picked', { count: picked.length })}</span>
          </span>
          {picked.length > 0 && (
            <div className="tl-picked">
              {picked.map((k) => (
                <button key={k} type="button" className="dl-chip" onClick={() => toggle(k)}>
                  {refOf(k).kind === 'trial' && <FeatherIcon name="gift" size={12} />}
                  <span className="dl-ellipsis">{known.get(k)?.name ?? `#${refOf(k).link_id}`}</span>
                  <FeatherIcon name="x" size={12} />
                </button>
              ))}
            </div>
          )}
          <div className="tl-pick-bar">
            <div className="dl-seg" role="group" aria-label={t('report.kind')}>
              {KINDS.map((k) => (
                <button key={k} type="button" className={`dl-seg-btn ${kind === k ? 'is-on' : ''}`} aria-pressed={kind === k} onClick={() => setKind(k)}>
                  {t(`tabs.${k}`)}
                  {pickedOf(k) > 0 && <span className="tl-seg-count">{pickedOf(k)}</span>}
                </button>
              ))}
            </div>
            <label className="dl-search">
              <FeatherIcon name="search" size={14} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('report.searchLinks')} aria-label={t('report.searchLinks')} />
            </label>
          </div>
          <div className="dl-checklist tl-checklist">
            {loadError ? (
              <p className="dl-error dl-pad">{loadError}</p>
            ) : !links ? (
              <LoadingBlock pad="24px 0" label={t('loading')} />
            ) : groups.length === 0 ? (
              <p className="dl-muted dl-pad">{t('links.noMatch')}</p>
            ) : (
              groups.map(([accountId, rows]) => (
                <div key={accountId} className="tl-pick-group">
                  <div className="tl-pick-account dl-label">{accountLabel(accountId)}</div>
                  {rows.map((l) => {
                    const k = key(l)
                    const on = picked.includes(k)
                    return (
                      <label key={k} className={`dl-check-row ${on ? 'is-on' : ''}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(k)} />
                        <span className="dl-ellipsis">{l.name}</span>
                        {l.is_deleted && <span className="dl-badge dl-badge--quiet">{t('links.deleted')}</span>}
                        {l.archived && <span className="dl-badge dl-badge--quiet">{t('archive.badge')}</span>}
                        <span className="dl-muted tl-pick-count">{t('report.fansCount', { count: l.fans ?? 0 })}</span>
                      </label>
                    )
                  })}
                </div>
              ))
            )}
          </div>
          <span className="dl-hint">{t('report.kindsHint')}</span>
        </div>

        <fieldset className="dl-field tl-columns">
          <legend className="dl-label">{t('report.columns')}</legend>
          <div className="tl-column-list">
            {REPORT_COLUMNS.map((c) => (
              <label key={c} className="dl-check" title={t(`reportColHelp.${c}`, { defaultValue: t(`colHelp.${c}`, { since: '' }) })}>
                <input type="checkbox" checked={columns.includes(c)} onChange={() => toggleColumn(c)} />
                {t(`reportCol.${c}`)}
              </label>
            ))}
          </div>
          <span className="dl-hint">{t('report.columnsHint')}</span>
        </fieldset>

        <div className="dl-field tl-expiry">
          <label className="dl-label" htmlFor={`${ids}-expires`}>{t('report.expires')}</label>
          <input id={`${ids}-expires`} type="date" className="dl-input" value={expires} onChange={(e) => setExpires(e.target.value)} aria-describedby={`${ids}-expires-hint`} />
          <span id={`${ids}-expires-hint`} className="dl-hint">{t('report.expiresHint')}</span>
        </div>
      </div>
    </Modal>
  )
}

/** A row's share button: put the link in an existing report, or start a new one with it. */
export function AddToReportDialog({
  api,
  link,
  onClose,
  onNew,
  onAdded,
}: {
  api: TrackingLinksApi
  link: LinkRow
  onClose: () => void
  onNew: () => void
  onAdded: (report: LinkReport) => void
}) {
  const { t } = useTranslation('trackingLinks')
  const [reports, setReports] = useState<LinkReport[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const k = key(link)

  useEffect(() => {
    api.reports().then(
      (r) => setReports(r.reports),
      (e: unknown) => setError(e instanceof Error ? e.message : String(e)),
    )
  }, [api])

  const add = async (r: LinkReport) => {
    setBusy(r.id)
    setError('')
    try {
      const out = await api.updateReport(r.id, { links: [...r.links, { account_id: link.account_id, kind: link.kind, link_id: link.link_id }] })
      onAdded(out.report)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy('')
    }
  }

  return (
    <Modal
      title={t('share.title')}
      onClose={onClose}
      footer={
        <>
          {error && <p className="dl-error dl-foot-error">{error}</p>}
          <button type="button" className="dl-btn dl-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="dl-btn dl-btn--primary" onClick={onNew}>
            <FeatherIcon name="plus" size={14} /> {t('share.newReport')}
          </button>
        </>
      }
    >
      <div className="tl-form">
        <p className="dl-hint">{t('share.hint', { name: link.name })}</p>
        {!reports ? (
          !error && <LoadingBlock label={t('loading')} />
        ) : reports.length === 0 ? (
          <p className="dl-muted">{t('share.noReports')}</p>
        ) : (
          <ul className="tl-share-list">
            {reports.map((r) => {
              const has = r.links.some((l) => key(l) === k)
              return (
                <li key={r.id} className={r.expired ? 'tl-expired' : ''}>
                  <span className="dl-strong dl-ellipsis">{r.name}</span>
                  <span className="dl-muted">{t('reports.links', { count: r.links.length })}</span>
                  {has ? (
                    <span className="dl-badge dl-badge--quiet">{t('share.already')}</span>
                  ) : (
                    <button type="button" className="dl-btn dl-btn--small" disabled={Boolean(busy)} onClick={() => void add(r)}>
                      <BusyLabel busy={busy === r.id} label={t('saving')}>{t('share.add')}</BusyLabel>
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Modal>
  )
}
